import { useEffect, useRef, useState } from "react";
import { Button } from "@progress/kendo-react-buttons";
import {
  gxUserprofileuploadApi,
  gxUserprofileviewApi,
  gxUserviewApi,
} from "@/app/apiConfig/services/customerAbstractApiLayer";
import type { UserView } from "@/app/types/userprofile";
import { ProfiledetailsLoading } from "@/app/components/webloading/shimmer/MyprofileLoading";
import { useToken } from "@/app/services/context/TokenContext";
import { redirectToGrowXLogout } from "@/app/utils/loginservice/redirectToGrowXLogout";
import { t } from "i18next";
import { useSelector } from "react-redux";
import type { RootState } from "@/app/redux/store";
import AvatarDisplay from "./AvatarDisplay";
import { useCompanyLogo } from "@/app/hooks/useCompanyLogo";

const Profiledetails = () => {
  const [profile, setProfile] = useState<UserView | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isImageLoading, setIsImageLoading] = useState(false);
  const { logout } = useToken();
  const userData = useSelector((state: RootState) => state.userData.data);

  // ........................profile view image state...................................
  const [profileViewImage, setProfileViewImage] = useState<string | null>(null);

  // ........................company logo (fallback avatar)...................................
  const { companyLogo } = useCompanyLogo(
    profile?.groupId as string | undefined,
  );

  // ........................photo update state...................................
  const [showOptions, setShowOptions] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [cameraError, setCameraError] = useState<string>("");
  const streamRef = useRef<MediaStream | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ........................storage key (namespaced per user)...................................

  const photoStorageKey = `gx_profile_photo_${userData?.userId ?? "guest"}`;

  // ........................handleLogout...................................
  const handleLogout = () => {
    logout();
    setTimeout(redirectToGrowXLogout, 0);
  };

  // ........................LoginTime...................................

  const date = profile?.userLastLoginTime
    ? new Date(profile?.userLastLoginTime).toLocaleString("en-US", {
        weekday: "short",
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      })
    : "N/A";

  // ........................getUserApi...................................

  const getUserApi = async () => {
    setIsLoading(true);
    const UserID = userData?.userId;
    try {
      const requestPayload = { userId: UserID };
      const response = await gxUserviewApi(requestPayload);
      if (response?.data) {
        const storedPhoto = localStorage.getItem(photoStorageKey);
        setProfile({
          ...response.data,
          profileImageUrl: storedPhoto || response.data.profileImageUrl,
        });
        setIsLoading(false);
      }
    } catch (error) {
      console.log(error);
      setIsLoading(false);
    }
  };

  // ....................................handleupdate........................................

  const handleupdate = () => {
    setCameraError("");
    setShowOptions((prev) => !prev);
  };

  // ....................................helper: file -> base64........................................

  const fileToBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  // ....................................applyNewPhoto........................................

  const applyNewPhoto = async (file: File) => {
    try {
      setIsImageLoading(true);
      const formData = new FormData();
      formData.append("file", file);
      formData.append("documentType", "USER PROFILE");
      const response = await gxUserprofileuploadApi(formData);

      if (response.status == 1) {
        const uploadedUrl =
          response?.data?.profileImageUrl || response?.data?.fileUrl;
        getprofileview();
        getUserApi();
        if (uploadedUrl) {
          setProfile((prev) =>
            prev ? { ...prev, profileImageUrl: uploadedUrl } : prev,
          );
        } else {
          const base64 = await fileToBase64(file);
          setProfile((prev) =>
            prev ? { ...prev, profileImageUrl: base64 } : prev,
          );
        }
      }
    } catch (error) {
      console.log(error);
    }
  };

  // ....................................removePhoto........................................

  const removePhoto = async () => {
    try {
      const formData = new FormData();
      formData.append("documentType", "USER PROFILE");
      formData.append("actionType", "Delete");

      const response = await gxUserprofileuploadApi(formData);

      if (response?.status === 1) {
        sessionStorage.removeItem("profileView");
        setProfileViewImage("");
        getprofileview();
        getUserApi()
      }
    } catch (error) {
      console.error("Remove photo error:", error);
    }
  };

  // ....................................camera handlers........................................

  const openCamera = async () => {
    setShowOptions(false);
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: true,
      });
      streamRef.current = mediaStream;
      setShowCamera(true);
    } catch (error) {
      setCameraError(t("camera_permission_error") || "Unable to access camera");
      setShowOptions(true);
    }
  };

  useEffect(() => {
    if (showCamera && videoRef.current && streamRef.current) {
      videoRef.current.srcObject = streamRef.current;
    }
  }, [showCamera]);

  const closeCamera = () => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    setShowCamera(false);
  };

  const capturePhoto = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext("2d");
    ctx?.drawImage(video, 0, 0, canvas.width, canvas.height);

    canvas.toBlob((blob) => {
      if (blob) {
        const file = new File([blob], `profile-photo-${Date.now()}.png`, {
          type: "image/png",
        });
        applyNewPhoto(file);
      }
    }, "image/png");

    closeCamera();
  };

  // ....................................file upload handlers........................................

  const triggerFileUpload = () => {
    setShowOptions(false);
    fileInputRef.current?.click();
  };

  const handleFileSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      applyNewPhoto(e.target.files[0]);
    }
    e.target.value = "";
  };

  // ........................getprofileview...................................

  const getprofileview = async () => {
    setIsImageLoading(true);
    try {
      const requestPayload = { documentType: "USER PROFILe" };
      const response = await gxUserprofileviewApi(requestPayload);
      const doc = Array.isArray(response?.data)
        ? response.data[0]
        : response?.data;

      if (doc?.base64Output) {
        const ext = doc.fileName?.split(".").pop()?.toLowerCase();

        const mime =
          ext === "jpg" || ext === "jpeg"
            ? "image/jpeg"
            : ext === "gif"
              ? "image/gif"
              : "image/png";
        const imageUrl = `data:${mime};base64,${doc.base64Output}`;
        setProfileViewImage(imageUrl);
      } else {
        setProfileViewImage("");
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsImageLoading(false);
    }
  };

  useEffect(() => {
    if (profile?.profileImageName) {
      getprofileview();
    }
  }, [profile]);

  useEffect(() => {
    getUserApi();
  }, []);

  useEffect(() => {
    if (profile?.roleName) {
      sessionStorage.setItem("roleName", profile?.roleName);
    }
  }, [profile]);

  useEffect(() => {
    return () => {
      streamRef.current?.getTracks().forEach((track) => track.stop());
    };
  }, []);

  // ........................sync to sessionStorage + notify Header (no reload)...................................

  useEffect(() => {
    if (profileViewImage) {
      sessionStorage.setItem("profileView", profileViewImage);
    } else {
      sessionStorage.removeItem("profileView");
    }

    window.dispatchEvent(
      new CustomEvent("profilePhotoUpdated", {
        detail: profileViewImage || "",
      }),
    );
  }, [profileViewImage]);

  return (
    <div className="gx-profiledetails-card">
      {isLoading ? (
        <ProfiledetailsLoading />
      ) : (
        <div className="grid grid-cols-12">
          <div className="col-span-12">
            <div className="gx-profile">
              <div
                className={`gx-avatar-wrapper ${isImageLoading ? "uploading" : ""} `}
              >
                <AvatarDisplay
                  profilePhoto={profileViewImage || null}
                  companyLogo={companyLogo}
                  firstName={profile?.firstName}
                  lastName={profile?.lastName}
                  size={84}
                />

                {!isImageLoading && (
                  <button
                    type="button"
                    className="gx-avatar-edit-btn"
                    aria-label={t("edit_photo")}
                    onClick={handleupdate}
                  >
                    +
                  </button>
                )}

                {/* ........................options popup........................ */}
                {showOptions && (
                  <div className="gx-photo-popup">
                    {cameraError && (
                      <p className="gx-photo-popup-error">{cameraError}</p>
                    )}

                    <div className="gx-photo-popup-options">
                      <button
                        type="button"
                        onClick={openCamera}
                        className="gx-photo-popup-option"
                      >
                        Take photo
                      </button>

                      <button
                        type="button"
                        onClick={triggerFileUpload}
                        className="gx-photo-popup-option"
                      >
                        Upload file
                      </button>

                      <button
                        type="button"
                        onClick={removePhoto}
                        className="gx-photo-popup-option gx-photo-popup-option--danger"
                      >
                        Remove photo
                      </button>
                    </div>
                  </div>
                )}

                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  accept="image/*"
                  onChange={handleFileSelected}
                />
              </div>
            </div>
          </div>

          <div className="col-span-12">
            <div className="gx-profile-name">
              <h3 className="gx-h3-b">
                {profile?.firstName} {profile?.lastName}
              </h3>
              <p className="gx-p14-md">
                {t("last_login")} : {date}
              </p>
            </div>
          </div>

          <div className="col-span-12">
            <div className="gx-profile">
              <div className="gx-profile-preparer">
                <p className="gx-h5-sb">{profile?.roleName}</p>
              </div>
            </div>
          </div>

          <div className="col-span-12">
            <div className="gx-profile-logout">
              <Button
                className="gx-p12-eb"
                onClick={handleLogout}
                type="button"
                fillMode={"outline"}
                themeColor={"base"}
              >
                {t("log_out")}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ........................camera modal........................ */}
      {showCamera && (
        <div className="gx-camera-modal-overlay">
          <div className="gx-camera-modal">
            <div className="gx-camera-modal-header">
              <p className="gx-camera-modal-title">
                {t("take_photo") || "Take photo"}
              </p>
              <button
                type="button"
                onClick={closeCamera}
                aria-label={t("close")}
                className="gx-photo-popup-close"
              >
                ×
              </button>
            </div>

            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="gx-camera-video"
            />
            <canvas ref={canvasRef} className="hidden" />

            <div className="gx-camera-modal-actions">
              <Button
                type="button"
                fillMode="outline"
                themeColor="base"
                onClick={closeCamera}
              >
                {t("cancel")}
              </Button>
              <Button type="button" themeColor="primary" onClick={capturePhoto}>
                {t("capture")}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Profiledetails;
